import { Select as SelectPrimitive } from '@base-ui/react/select'
import { useEffect, useRef, useState } from 'react'
import { PillDropdown } from '@/components/ui/pill-dropdown'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import { ChevronDownIcon } from './icons'
export const ROWS_PER_PAGE_PRESETS = [20, 50, 100] as const

/** Ceiling for a typed value, so a stray digit can't ask the API for millions. */
const MAX_ROWS_PER_PAGE = 1000

export type RowsPerPageValue = number

/**
 * Reads a rows-per-page search param. Anything unparseable falls back to the
 * default, so a hand-edited URL degrades to a working page instead of an error.
 */
export const parseRowsPerPage = (
	rawValue: string | undefined,
	fallback: number
): RowsPerPageValue => {
	const parsed = Number(rawValue)
	if (!Number.isInteger(parsed) || parsed < 1 || parsed > MAX_ROWS_PER_PAGE) {
		return fallback
	}

	return parsed
}

interface RowsPerPageSelectProps {
	value: RowsPerPageValue
	onChange: (value: RowsPerPageValue) => void
	className?: string
}

/**
 * Rows-per-page control: a select whose trigger is also a number input, so a
 * size that isn't on the preset list can be typed straight in. A typed size is
 * echoed back into the list as a "(custom)" entry, which is what tells the user
 * the odd number they see is theirs and still selected.
 *
 * A typed number out of range is clamped to the nearest end (1, or the hard
 * ceiling) rather than rejected; text that isn't a whole number at all restores
 * the size currently in effect.
 */
function RowsPerPageSelect({
	value,
	onChange,
	className
}: RowsPerPageSelectProps) {
	const { t } = useTranslation()
	const [draft, setDraft] = useState('')
	const inputRef = useRef<HTMLInputElement>(null)

	// A typed size that isn't one of the presets earns its own entry, so the
	// list can show it as selected. `undefined` whenever the current size is a
	// preset — there is nothing extra to offer then.
	const customValue = ROWS_PER_PAGE_PRESETS.some((preset) => preset === value)
		? undefined
		: value

	// Presets and the custom size share one ascending list, so a typed 40 sits
	// between 20 and 50 instead of being stranded after 100.
	const numericOptions =
		customValue === undefined
			? [...ROWS_PER_PAGE_PRESETS]
			: [...ROWS_PER_PAGE_PRESETS, customValue].sort((a, b) => a - b)

	// The input is uncontrolled while the user types and snaps back to the
	// committed value on blur, so a half-typed "1" of "150" is never treated as
	// a real page size.
	useEffect(() => {
		setDraft(String(value))
	}, [value])

	const commitDraft = () => {
		const parsed = Number(draft.trim())

		// Only a number that isn't one — empty, letters, a decimal — is rejected.
		// A number merely out of range is a legible intent, so it is clamped
		// rather than thrown away: typing 5000 lands on the largest page the
		// control can ask for, not back on the old value.
		//
		// The clamp is deliberately blind to how many rows actually exist: a page
		// size is a preference, not a claim about the result set. Capping 100
		// down to a small result would rewrite the user's choice — and, since
		// committing runs on blur, it would do so as they clicked a preset in
		// the list, overwriting the very value they had just picked.
		if (!(draft.trim() && Number.isInteger(parsed))) {
			setDraft(String(value))
			return
		}

		const clamped = Math.min(Math.max(parsed, 1), MAX_ROWS_PER_PAGE)

		if (clamped === value) {
			// Same size as before — snap the text back, since the draft that
			// produced it ("5000") isn't what is in effect.
			setDraft(String(clamped))
			return
		}

		onChange(clamped)
	}

	return (
		<div className={cn('flex items-center gap-2', className)}>
			<span className='shrink-0 text-grey-700 text-sm/4.5'>
				{t('common:rows_per_page')}
			</span>

			<Select
				onValueChange={(newValue: string | null) => {
					if (newValue === null) {
						return
					}
					onChange(Number(newValue))
				}}
				value={String(value)}
			>
				{/* The label belongs on the trigger, which is what assistive
				    tech announces — the pill inside it is presentational. */}
				<SelectTrigger aria-label={t('common:rows_per_page')} bare>
					<PillDropdown className='min-w-14' size='sm'>
						<SelectValue className='w-full flex-none items-center justify-between gap-0.5'>
							{/* The value slot is the input itself: clicking the
						    chevron still opens the list, clicking the number
						    puts a caret in it. */}
							<input
								aria-label={t('common:rows_per_page')}
								// `min-w-[2ch]` is the floor for `field-sizing-content`,
								// which sizes to the text and ignores `size`; the
								// `size` attribute below covers Safari and Firefox,
								// which don't implement field-sizing yet.
								className='field-sizing-content min-w-[3ch] bg-transparent outline-none'
								inputMode='numeric'
								onBlur={commitDraft}
								onChange={(event) =>
									setDraft(event.target.value)
								}
								onClick={(event) => {
									event.stopPropagation()
									inputRef.current?.select()
								}}
								onKeyDown={(event) => {
									if (event.key === 'Enter') {
										event.preventDefault()
										commitDraft()
										inputRef.current?.blur()
										return
									}
									if (event.key === 'Escape') {
										setDraft(String(value))
										inputRef.current?.blur()
										return
									}
									// Let the field own typing and caret motion;
									// anything else (arrows, Home/End) belongs to the
									// select so the list stays keyboard-navigable.
									if (
										event.key.length === 1 ||
										event.key === 'Backspace' ||
										event.key === 'Delete'
									) {
										event.stopPropagation()
									}
								}}
								onPointerDown={(event) =>
									event.stopPropagation()
								}
								ref={inputRef}
								// Never narrower than two characters: a one-digit
								// field is a hard target to click into and to read.
								size={Math.max(draft.length, 2)}
								value={draft}
							/>
							<SelectPrimitive.Icon className='shrink-0'>
								<ChevronDownIcon className='in-data-popup-open:rotate-180 transition-transform duration-150' />
							</SelectPrimitive.Icon>
						</SelectValue>
					</PillDropdown>
				</SelectTrigger>

				<SelectContent className='w-45'>
					{numericOptions.map((option) => (
						<SelectItem key={option} value={String(option)}>
							{option === customValue
								? t('common:rows_per_page_custom', {
										value: option
									})
								: option}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	)
}

export { RowsPerPageSelect }
